'use es6';

import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { FieldTypeNames } from 'ui-forms-components/constants/FieldTypeMapping';
import { View } from 'foundations-components/transitional/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Small } from 'foundations-components/transitional/components/Small';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { CommentableArea } from 'collaboration-sidebar';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { DefaultValidation } from 'FormsUI/constants/FormFieldValidations';
import { addFieldValidationErrorIfNeeded, removeFieldValidationErrorIfNeeded } from 'FormsUI/actions/FormDefinition';
import { ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import { getFieldMinValidation, getFieldMaxValidation } from 'FormsUI/selectors/FormDefinition';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const undefToString = value => {
  return value === undefined ? '' : value;
};
class FormFieldMinMaxConfigurationFieldInput extends PureComponent {
  constructor(props) {
    super(props);
    this.focusIntoMax = this.focusIntoMax.bind(this);
    this.maxInputRef = /*#__PURE__*/createRef();
  }
  componentDidUpdate(prevProps) {
    const previousActive = this.getMaxAndMinAndActive(prevProps.currentValue).active;
    const nextActive = this.getMaxAndMinAndActive(this.props.currentValue).active;
    if (!previousActive && nextActive) {
      this.focusIntoMax();
    }
  }
  langKey(suffix) {
    return `editor.form.config.${suffix}`;
  }
  formatAsString(oMin, oMax, oActive) {
    const asString = `${undefToString(oMin)}:${undefToString(oMax)}:${undefToString(oActive)}`;
    const {
      min,
      max,
      active
    } = this.getMaxAndMinAndActive(asString);
    return `${min}:${max}:${active}`;
  }
  getMaxAndMinAndActive(currentValue) {
    const values = currentValue.split(':');
    const {
      fieldType
    } = this.props;
    let defaultMin = 0;
    let defaultMax = 0;
    if (fieldType === FieldTypes.PHONE) {
      defaultMin = DefaultValidation[FieldTypes.PHONE].MIN_LENGTH;
      defaultMax = DefaultValidation[FieldTypes.PHONE].MAX_LENGTH;
    }
    if (isEmpty(currentValue)) {
      return {
        active: false,
        max: defaultMax,
        min: defaultMin
      };
    }
    values[0] = parseFloat(values[0], '10');
    values[1] = parseFloat(values[1], '10');
    return {
      active: values[2] === 'true',
      max: !isNaN(values[1]) ? values[1] : defaultMax,
      min: !isNaN(values[0]) ? values[0] : defaultMin
    };
  }
  validateMinMaxValues(min, max) {
    const {
      currentValue
    } = this.props;
    const currentMinMaxActive = this.getMaxAndMinAndActive(currentValue);
    const {
      fieldName,
      fieldObjectTypeId,
      addFieldValidationError,
      removeFieldValidationError
    } = this.props;
    if (!isEqual(currentMinMaxActive.min, min) && min > max) {
      addFieldValidationError(fieldName, fieldObjectTypeId, ValidationKeys.FIELD_MIN_GREATER_THAN_MAX, ValidationType.INVALID, {
        fieldName,
        fieldObjectTypeId
      });
    } else if (!isEqual(currentMinMaxActive.max, max) && max < min) {
      addFieldValidationError(fieldName, fieldObjectTypeId, ValidationKeys.FIELD_MAX_LESS_THAN_MIN, ValidationType.INVALID, {
        fieldName,
        fieldObjectTypeId
      });
    } else {
      removeFieldValidationError(fieldName, fieldObjectTypeId, ValidationKeys.FIELD_MIN_GREATER_THAN_MAX, ValidationType.INVALID, {
        fieldName,
        fieldObjectTypeId
      });
      removeFieldValidationError(fieldName, fieldObjectTypeId, ValidationKeys.FIELD_MAX_LESS_THAN_MIN, ValidationType.INVALID, {
        fieldName,
        fieldObjectTypeId
      });
    }
  }
  handleValidationChange(min, max, active) {
    const {
      handleChange
    } = this.props;
    const newValue = this.formatAsString(min, max, active);
    this.validateMinMaxValues(min, max);
    handleChange(newValue);
  }
  focusIntoMax() {
    this.maxInputRef.current.focus();
    this.maxInputRef.current.select();
  }
  maybeRenderValidationErrors() {
    const {
      formFieldMinValidation,
      formFieldMaxValidation
    } = this.props;
    const minValidationMessage = isEmpty(formFieldMinValidation) ? null : /*#__PURE__*/_jsx(Small, {
      use: "error",
      "data-test-id": "number-validation-min-invalid",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.validation.minValue.invalid"
      })
    });
    const maxValidationMessage = isEmpty(formFieldMaxValidation) ? null : /*#__PURE__*/_jsx(Small, {
      use: "error",
      "data-test-id": "number-validation-max-invalid",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.validation.maxValue.invalid"
      })
    });
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("div", {
        children: minValidationMessage
      }), /*#__PURE__*/_jsx("div", {
        children: maxValidationMessage
      })]
    });
  }
  maybeRenderDisableCheckbox() {
    const {
      label,
      tooltip,
      hideDisableCheckbox,
      currentValue
    } = this.props;
    const {
      min,
      max,
      active
    } = this.getMaxAndMinAndActive(currentValue);
    if (hideDisableCheckbox) {
      return null;
    }
    return /*#__PURE__*/_jsxs(Checkbox, {
      id: "number-limit",
      "data-test-id": "number-validation-toggle",
      onChange: ({
        target: {
          checked
        }
      }) => {
        this.handleValidationChange(min, max, checked);
      },
      checked: active,
      children: [label, ' ', /*#__PURE__*/_jsx(HelpIcon, {
        size: "300",
        title: tooltip,
        alignWithText: true,
        padding: "0"
      })]
    });
  }
  renderNumberRangeInputs(min, max, active) {
    const usedMin = active ? min : undefined;
    const usedMax = active ? max : undefined;
    return /*#__PURE__*/_jsxs(TFlex, {
      justify: "around",
      children: [/*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('min')
          }),
          children: /*#__PURE__*/_jsx(StepperInput, {
            "data-test-id": "number-validation-min",
            onChange: ({
              target: {
                value
              }
            }) => this.handleValidationChange(value, max, active),
            value: usedMin,
            use: "on-dark",
            defaultValue: 0,
            disabled: !active
          })
        })
      }), /*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('max')
          }),
          children: /*#__PURE__*/_jsx(StepperInput, {
            "data-test-id": "number-validation-max",
            onChange: ({
              target: {
                value
              }
            }) => this.handleValidationChange(min, value, active),
            value: usedMax,
            use: "on-dark",
            defaultValue: 0,
            onFocus: this.handleMaxFocus,
            disabled: !active,
            inputRef: this.maxInputRef
          })
        })
      })]
    });
  }
  render() {
    const {
      currentValue,
      tooltip,
      label,
      fieldType,
      field
    } = this.props;
    const {
      min,
      max,
      active
    } = this.getMaxAndMinAndActive(currentValue);
    return /*#__PURE__*/_jsx(CommentableArea, {
      moduleId: `${getPropertyObjectTypeId(field)}-min-max`,
      description: {
        translationKey: 'formsMinMax'
      },
      children: /*#__PURE__*/_jsxs(View, {
        as: "div",
        "data-test-id": "config-input-field",
        children: [this.maybeRenderDisableCheckbox(fieldType, label, tooltip, min, max, active), this.renderNumberRangeInputs(min, max, active, fieldType), this.maybeRenderValidationErrors()]
      })
    });
  }
}
FormFieldMinMaxConfigurationFieldInput.propTypes = {
  fieldType: PropTypes.oneOf(FieldTypeNames).isRequired,
  label: PropTypes.object.isRequired,
  tooltip: PropTypes.object,
  currentValue: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  field: PropTypes.object,
  fieldName: PropTypes.string,
  fieldObjectTypeId: PropTypes.string,
  addFieldValidationError: PropTypes.func.isRequired,
  formFieldMaxValidation: PropTypes.func,
  formFieldMinValidation: PropTypes.func,
  removeFieldValidationError: PropTypes.func.isRequired,
  hideDisableCheckbox: PropTypes.bool
};
const mapStateToProps = (state, {
  fieldName,
  fieldObjectTypeId
}) => ({
  formFieldMinValidation: getFieldMinValidation(state, fieldName, fieldObjectTypeId),
  formFieldMaxValidation: getFieldMaxValidation(state, fieldName, fieldObjectTypeId)
});
const actionCreators = {
  addFieldValidationError: addFieldValidationErrorIfNeeded,
  removeFieldValidationError: removeFieldValidationErrorIfNeeded
};
export default connect(mapStateToProps, actionCreators)(FormFieldMinMaxConfigurationFieldInput);